import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { OnboardingResourceCategory } from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GripVertical, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { CATEGORY_OPTIONS } from "@/lib/category-labels";
import { orpc } from "@/lib/rpc";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceId: string | null;
  /** Called after a successful save so the parent can invalidate its lists. */
  onSaved: () => void;
};

type DueRule = "day_of" | "next_day" | "this_week" | "specific_date" | "no_deadline";

function isValidHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

type SourceLink = { id: string; url: string };
type ChannelDraft = { id: string; name: string; description: string; nameTouched: boolean };
type ToolDraft = {
  id: string;
  name: string;
  helpLink: string;
  description: string;
  required: boolean;
  nameTouched: boolean;
  linkTouched: boolean;
};

function newId() {
  return crypto.randomUUID();
}

type SortableChannelRowProps = {
  channel: ChannelDraft;
  index: number;
  canRemove: boolean;
  submitted: boolean;
  onChange: (id: string, patch: Partial<ChannelDraft>) => void;
  onRemove: (id: string) => void;
};

function SortableChannelRow({
  channel,
  index,
  canRemove,
  submitted,
  onChange,
  onRemove,
}: SortableChannelRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: channel.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-md border border-border p-3 space-y-2 bg-background ${
        isDragging ? "opacity-80 shadow-md" : ""
      }`}
      data-testid={`edit-slack-channel-${index}`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none p-1 text-muted-foreground hover:text-foreground active:cursor-grabbing"
          aria-label="Reordenar canal"
          data-testid={`edit-slack-channel-drag-${index}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <Input
          value={channel.name}
          onChange={(e) => onChange(channel.id, { name: e.target.value })}
          onBlur={() => onChange(channel.id, { nameTouched: true })}
          placeholder="#nome-do-canal"
          data-testid={`edit-slack-channel-name-${index}`}
        />
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onRemove(channel.id)}
            aria-label="Remover canal"
            data-testid={`edit-slack-channel-remove-${index}`}
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>
      {(channel.nameTouched || submitted) && channel.name.trim().length === 0 && (
        <p className="text-xs text-destructive">Nome do canal é obrigatório.</p>
      )}
      <Textarea
        value={channel.description}
        onChange={(e) => onChange(channel.id, { description: e.target.value })}
        placeholder="Descrição (opcional)"
        rows={2}
        data-testid={`edit-slack-channel-desc-${index}`}
      />
    </div>
  );
}

type SortableToolRowProps = {
  tool: ToolDraft;
  index: number;
  canRemove: boolean;
  submitted: boolean;
  linkError: boolean;
  onChange: (id: string, patch: Partial<ToolDraft>) => void;
  onRemove: (id: string) => void;
};

function SortableToolRow({
  tool,
  index,
  canRemove,
  submitted,
  linkError,
  onChange,
  onRemove,
}: SortableToolRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tool.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-md border border-border p-3 space-y-2 bg-background ${
        isDragging ? "opacity-80 shadow-md" : ""
      }`}
      data-testid={`edit-tool-row-${index}`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none p-1 text-muted-foreground hover:text-foreground active:cursor-grabbing"
          aria-label="Reordenar ferramenta"
          data-testid={`edit-tool-drag-${index}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <Input
          value={tool.name}
          onChange={(e) => onChange(tool.id, { name: e.target.value })}
          onBlur={() => onChange(tool.id, { nameTouched: true })}
          placeholder="Nome da ferramenta"
          data-testid={`edit-tool-name-${index}`}
        />
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onRemove(tool.id)}
            aria-label="Remover ferramenta"
            data-testid={`edit-tool-remove-${index}`}
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>
      {(tool.nameTouched || submitted) && tool.name.trim().length === 0 && (
        <p className="text-xs text-destructive">Nome da ferramenta é obrigatório.</p>
      )}
      <Input
        type="text"
        inputMode="url"
        value={tool.helpLink}
        onChange={(e) => onChange(tool.id, { helpLink: e.target.value })}
        onBlur={() => onChange(tool.id, { linkTouched: true })}
        placeholder="Link de ajuda (opcional)"
        data-testid={`edit-tool-link-${index}`}
      />
      {linkError && <p className="text-xs text-destructive">Informe uma URL http(s) válida.</p>}
      <Textarea
        value={tool.description}
        onChange={(e) => onChange(tool.id, { description: e.target.value })}
        placeholder="Descrição (opcional)"
        rows={2}
        data-testid={`edit-tool-desc-${index}`}
      />
      <label className="flex items-center gap-2 text-sm text-foreground">
        <Checkbox
          checked={tool.required}
          onCheckedChange={(checked) => onChange(tool.id, { required: checked === true })}
          data-testid={`edit-tool-required-${index}`}
        />
        <span>Obrigatória</span>
      </label>
    </div>
  );
}

export function EditResourceDialog({ open, onOpenChange, resourceId, onSaved }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const queryOpts = orpc.onboardingResources.getForEdit.queryOptions({
    input: { resourceId: resourceId ?? "" },
  });
  const resourceQuery = useQuery({ ...queryOpts, enabled: open && !!resourceId });
  const data = resourceQuery.data?.resource;

  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [url, setUrl] = useState("");
  const [briefing, setBriefing] = useState("");
  const [sourceLinks, setSourceLinks] = useState<SourceLink[]>([{ id: "init", url: "" }]);
  const [todoDescription, setTodoDescription] = useState("");
  const [todoLink, setTodoLink] = useState("");
  const [dueRule, setDueRule] = useState<DueRule | "">("");
  const [dueDate, setDueDate] = useState("");
  const [orientationCategory, setOrientationCategory] = useState<OnboardingResourceCategory | null>(
    null,
  );
  const [channels, setChannels] = useState<ChannelDraft[]>([]);
  const [tools, setTools] = useState<ToolDraft[]>([]);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open && data) {
      setTitle(data.title);
      setUrl(data.sourceUrl ?? "");

      const blocks = (data.contentBlocks ?? []) as Array<Record<string, unknown>>;

      const firstParagraph = blocks.find(
        (b): b is { type: "paragraph"; text: string } =>
          typeof b === "object" &&
          b !== null &&
          (b as { type?: unknown }).type === "paragraph" &&
          typeof (b as { text?: unknown }).text === "string",
      );
      setBriefing(firstParagraph?.text ?? "");

      const refs = (data.sourceRefs ?? []).map((r, idx) => ({
        id: `ref-${idx}`,
        url: r.url,
      }));
      setSourceLinks(refs.length > 0 ? refs : [{ id: "init", url: "" }]);

      const todoBlock = blocks.find((b) => b?.type === "todo");
      setTodoDescription(((todoBlock?.description as string | null | undefined) ?? "") || "");
      setTodoLink(((todoBlock?.link as string | null | undefined) ?? "") || "");
      const persistedDueRule = todoBlock?.dueRule as DueRule | null | undefined;
      const persistedDueDate = todoBlock?.dueDate as string | null | undefined;
      setDueRule(persistedDueRule ?? (persistedDueDate ? "specific_date" : ""));
      setDueDate(persistedDueDate ?? "");

      setOrientationCategory(
        (data.category as OnboardingResourceCategory | null | undefined) ?? null,
      );

      const slackList = (
        (data.slackChannels ?? []) as Array<{
          name: string;
          description?: string | null;
        }>
      ).map((c) => ({
        id: newId(),
        name: c.name,
        description: c.description ?? "",
        nameTouched: false,
      }));
      setChannels(
        slackList.length > 0
          ? slackList
          : [{ id: newId(), name: "", description: "", nameTouched: false }],
      );

      const toolList = (
        (data.tools ?? []) as Array<{
          name: string;
          helpLink?: string | null;
          description?: string | null;
          required?: boolean;
        }>
      ).map((t) => ({
        id: newId(),
        name: t.name,
        helpLink: t.helpLink ?? "",
        description: t.description ?? "",
        required: t.required ?? true,
        nameTouched: false,
        linkTouched: false,
      }));
      setTools(
        toolList.length > 0
          ? toolList
          : [
              {
                id: newId(),
                name: "",
                helpLink: "",
                description: "",
                required: true,
                nameTouched: false,
                linkTouched: false,
              },
            ],
      );

      setTitleTouched(false);
      setSubmitted(false);
    }
  }, [open, data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleChannelDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setChannels((prev) => {
      const oldIndex = prev.findIndex((c) => c.id === active.id);
      const newIndex = prev.findIndex((c) => c.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  function handleToolDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setTools((prev) => {
      const oldIndex = prev.findIndex((t) => t.id === active.id);
      const newIndex = prev.findIndex((t) => t.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  const updateMutation = useMutation(orpc.onboardingResources.update.mutationOptions());
  const setCategoryMutation = useMutation(orpc.onboardingResources.setCategory.mutationOptions());

  const isRedirect = data?.behavior === "redirect";
  const isNotionMirror = data?.renderType === "notion_mirror";
  const acceptsUrl = isRedirect || isNotionMirror;
  const isPage = data?.behavior === "composed" && data?.renderType === "rich_doc";
  const isTodo = data?.renderType === "todo";
  const isSlack = data?.renderType === "slack_channel_info";
  const isTool = data?.renderType === "tool_setup" || data?.renderType === "setup_checklist";

  const trimmedTitle = title.trim();
  const trimmedUrl = url.trim();
  const trimmedTodoLink = todoLink.trim();
  const titleError = (titleTouched || submitted) && trimmedTitle.length === 0;
  const urlError = submitted && acceptsUrl && !isValidHttpUrl(trimmedUrl);
  const todoLinkError =
    submitted && isTodo && trimmedTodoLink.length > 0 && !isValidHttpUrl(trimmedTodoLink);
  const dueDateError =
    submitted && isTodo && dueRule === "specific_date" && dueDate.trim().length === 0;

  const validChannels = channels
    .map((c) => ({ name: c.name.trim(), description: c.description.trim() }))
    .filter((c) => c.name.length > 0);
  const channelsError = submitted && isSlack && validChannels.length === 0;

  const normalizedTools = tools.map((t) => ({
    name: t.name.trim(),
    helpLink: t.helpLink.trim(),
    description: t.description.trim(),
    required: t.required,
  }));
  const validTools = normalizedTools.filter((t) => t.name.length > 0);
  const toolsError = submitted && isTool && validTools.length === 0;
  const toolLinkErrors = tools.map((t, i) => {
    const link = normalizedTools[i].helpLink;
    const show = t.linkTouched || submitted;
    return show && link.length > 0 && !isValidHttpUrl(link);
  });
  const hasToolLinkError = isTool && toolLinkErrors.some(Boolean);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!resourceId || !data) return;
    setSubmitted(true);
    if (trimmedTitle.length === 0) return;
    if (acceptsUrl && !isValidHttpUrl(trimmedUrl)) return;
    if (isTodo) {
      if (trimmedTodoLink.length > 0 && !isValidHttpUrl(trimmedTodoLink)) return;
      if (dueRule === "specific_date" && dueDate.trim().length === 0) return;
    }
    if (isSlack && validChannels.length === 0) return;
    if (isTool) {
      if (validTools.length === 0) return;
      if (hasToolLinkError) return;
    }

    try {
      const payload: Parameters<typeof updateMutation.mutateAsync>[0] = {
        resourceId,
        title: trimmedTitle,
      };
      if (acceptsUrl) {
        payload.sourceUrl = trimmedUrl;
      }
      if (isPage) {
        payload.briefing = briefing.trim();
        payload.sourceRefs = sourceLinks
          .map((l) => l.url.trim())
          .filter((u) => isValidHttpUrl(u))
          .map((u) => ({ url: u, type: "url" as const }));
      }
      if (isTodo) {
        payload.todoContent = {
          description: todoDescription.trim() || null,
          link: trimmedTodoLink || null,
          dueRule: dueRule === "" ? null : dueRule,
          dueDate: dueRule === "specific_date" ? dueDate.trim() || null : null,
        };
      }
      if (isSlack) {
        payload.slackChannels = validChannels.map((c) => ({
          name: c.name,
          description: c.description.length > 0 ? c.description : null,
        }));
      }
      if (isTool) {
        payload.tools = validTools.map((t) => ({
          name: t.name,
          helpLink: t.helpLink.length > 0 ? t.helpLink : null,
          description: t.description.length > 0 ? t.description : null,
          required: t.required,
        }));
      }
      await updateMutation.mutateAsync(payload);

      const currentCategory =
        (data.category as OnboardingResourceCategory | null | undefined) ?? null;
      if (orientationCategory !== currentCategory) {
        await setCategoryMutation.mutateAsync({
          resourceId,
          category: orientationCategory,
        });
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryOpts.queryKey }),
        queryClient.invalidateQueries({
          queryKey: orpc.port.getContent.queryOptions({ input: { resourceId } }).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: orpc.port.getSidebar.queryOptions().queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: orpc.preview.getResourcePreview.queryOptions().queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: orpc.preview.getTemplatePreview.queryOptions().queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: orpc.preview.getOnboardingPreview.queryOptions().queryKey,
        }),
      ]);
      onSaved();
      onOpenChange(false);
      toast({ title: "Recurso atualizado" });
    } catch (err) {
      const message = (err as { message?: string } | null)?.message;
      toast({
        title: "Não foi possível atualizar",
        description: message,
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[760px] w-[92vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar recurso</DialogTitle>
          <DialogDescription>Ajuste o título e o conteúdo do recurso.</DialogDescription>
        </DialogHeader>

        {resourceQuery.isLoading || !data ? (
          <p className="py-6 text-sm text-muted-foreground text-center">Carregando…</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-resource-title">Título</Label>
              <Input
                id="edit-resource-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => setTitleTouched(true)}
                data-testid="edit-resource-title"
              />
              {titleError && <p className="text-xs text-destructive">Título é obrigatório.</p>}
            </div>

            {acceptsUrl && (
              <div className="space-y-2">
                <Label htmlFor="edit-resource-url">
                  {isNotionMirror ? "URL da página Notion" : "URL"}
                </Label>
                <Input
                  id="edit-resource-url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                  data-testid="edit-resource-url"
                />
                {urlError && (
                  <p className="text-xs text-destructive">Informe uma URL http(s) válida.</p>
                )}
              </div>
            )}

            {isPage && (
              <>
                <div className="space-y-2">
                  <Label>Links de referência</Label>
                  <div className="space-y-2">
                    {sourceLinks.map((link, idx) => (
                      <div key={link.id} className="flex gap-2">
                        <Input
                          type="url"
                          value={link.url}
                          onChange={(e) => {
                            const next = [...sourceLinks];
                            next[idx] = { ...next[idx], url: e.target.value };
                            setSourceLinks(next);
                          }}
                          placeholder="https://..."
                        />
                        {sourceLinks.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setSourceLinks(sourceLinks.filter((l) => l.id !== link.id))
                            }
                          >
                            Remover
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setSourceLinks([...sourceLinks, { id: crypto.randomUUID(), url: "" }])
                      }
                    >
                      + Adicionar link
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-resource-briefing">Briefing</Label>
                  <Textarea
                    id="edit-resource-briefing"
                    value={briefing}
                    onChange={(e) => setBriefing(e.target.value)}
                    rows={4}
                    data-testid="edit-resource-briefing"
                  />
                  <p className="text-xs text-muted-foreground">
                    Conteúdo livre — substitui o briefing atual. Para edição rica, use o MCP.
                  </p>
                </div>
              </>
            )}

            {isTodo && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="edit-resource-todo-description">Descrição (opcional)</Label>
                  <Textarea
                    id="edit-resource-todo-description"
                    value={todoDescription}
                    onChange={(e) => setTodoDescription(e.target.value)}
                    placeholder="Detalhes sobre o que precisa ser feito"
                    rows={3}
                    data-testid="edit-resource-todo-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-resource-todo-link">Link (opcional)</Label>
                  <Input
                    id="edit-resource-todo-link"
                    type="url"
                    value={todoLink}
                    onChange={(e) => setTodoLink(e.target.value)}
                    placeholder="https://..."
                    data-testid="edit-resource-todo-link"
                  />
                  {todoLinkError && (
                    <p className="text-xs text-destructive">Informe uma URL http(s) válida.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-resource-todo-due">Prazo (opcional)</Label>
                  <Select value={dueRule} onValueChange={(v) => setDueRule(v as DueRule | "")}>
                    <SelectTrigger id="edit-resource-todo-due" data-testid="edit-resource-todo-due">
                      <SelectValue placeholder="No dia do milestone (padrão)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day_of">No dia do milestone</SelectItem>
                      <SelectItem value="next_day">Dia seguinte</SelectItem>
                      <SelectItem value="this_week">Na semana</SelectItem>
                      <SelectItem value="specific_date">Data específica</SelectItem>
                      <SelectItem value="no_deadline">Sem prazo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {dueRule === "specific_date" && (
                  <div className="space-y-2">
                    <Label htmlFor="edit-resource-todo-due-date">Data</Label>
                    <Input
                      id="edit-resource-todo-due-date"
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      data-testid="edit-resource-todo-due-date"
                    />
                    {dueDateError && <p className="text-xs text-destructive">Informe uma data.</p>}
                  </div>
                )}
              </>
            )}

            {isSlack && (
              <div className="space-y-2">
                <Label>Canais</Label>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleChannelDragEnd}
                >
                  <SortableContext
                    items={channels.map((c) => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {channels.map((channel, index) => (
                        <SortableChannelRow
                          key={channel.id}
                          channel={channel}
                          index={index}
                          canRemove={channels.length > 1}
                          submitted={submitted}
                          onChange={(id, patch) =>
                            setChannels((prev) =>
                              prev.map((c) => (c.id === id ? { ...c, ...patch } : c)),
                            )
                          }
                          onRemove={(id) => setChannels((prev) => prev.filter((c) => c.id !== id))}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setChannels((prev) => [...prev, { id: newId(), name: "", description: "" }])
                  }
                  data-testid="edit-slack-channel-add"
                >
                  + Adicionar canal
                </Button>
                {channelsError && (
                  <p className="text-xs text-destructive">Informe pelo menos um canal.</p>
                )}
              </div>
            )}

            {isTool && (
              <div className="space-y-2">
                <Label>Ferramentas</Label>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleToolDragEnd}
                >
                  <SortableContext
                    items={tools.map((t) => t.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {tools.map((tool, index) => (
                        <SortableToolRow
                          key={tool.id}
                          tool={tool}
                          index={index}
                          canRemove={tools.length > 1}
                          submitted={submitted}
                          linkError={toolLinkErrors[index]}
                          onChange={(id, patch) =>
                            setTools((prev) =>
                              prev.map((t) => (t.id === id ? { ...t, ...patch } : t)),
                            )
                          }
                          onRemove={(id) => setTools((prev) => prev.filter((t) => t.id !== id))}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setTools((prev) => [
                      ...prev,
                      { id: newId(), name: "", helpLink: "", description: "", required: true },
                    ])
                  }
                  data-testid="edit-tool-add"
                >
                  + Adicionar ferramenta
                </Button>
                {toolsError && (
                  <p className="text-xs text-destructive">Informe pelo menos uma ferramenta.</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Categoria</Label>
              <div className="rounded-md border border-border overflow-hidden">
                <ul className="divide-y divide-border">
                  {CATEGORY_OPTIONS.map((opt) => {
                    const selected = opt.value === orientationCategory;
                    return (
                      <li key={opt.value}>
                        <button
                          type="button"
                          onClick={() => setOrientationCategory(selected ? null : opt.value)}
                          className={
                            "w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors " +
                            (selected
                              ? "bg-[#feecee] font-medium text-foreground"
                              : "hover:bg-secondary text-foreground")
                          }
                          data-testid={`edit-resource-category-${opt.value}`}
                        >
                          <span
                            className={
                              "inline-flex h-4 w-4 items-center justify-center rounded-full border " +
                              (selected ? "border-[#ef4d56]" : "border-border")
                            }
                            aria-hidden
                          >
                            {selected ? (
                              <span className="h-2 w-2 rounded-full bg-[#ef4d56]" />
                            ) : null}
                          </span>
                          <span>{opt.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <p className="text-xs text-muted-foreground">
                Categorias são labels de orientação. Todo conteúdo fica visível para todos.
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={updateMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending || hasToolLinkError}
                data-testid="edit-resource-submit"
              >
                {updateMutation.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
